import type { IdMap } from '@b-stock/fe-core/bstock-next/queries'

import { CellClass } from './cellStyles'
import type { ColumnDefinition } from './types'

type ManifestColumnSpec = {
  // Overrides to be merged with default column def
  colDef?: Partial<ColumnDefinition>
  options?: {
    // Width applied after autoSizeAllColumns() — lets auto-size run normally
    // but then caps the column at a narrower default the user can expand.
    autoSizeWidth?: number
    // Determines whether tooltipValueGetter is set
    showTooltip?: boolean
  }
}

/**
 * Manifest table rendering is fundamentally data-driven -- it must be able to
 * render arbitrary attributes of unknown data type without code changes.
 * However, "known" attributes may have custom column defs -- defined here.
 *
 * When changing column widths or adding/removing specs, bump the `version`
 * in FullManifestPanel's `useAgGridMemory` call to force-reset persisted
 * state. Without this, clients will load stale column layouts.
 */
export const columnSpecs: IdMap<ManifestColumnSpec> = {
  'attributes.brandName': {
    colDef: {
      cellClass: [CellClass.Small, CellClass.Dark, CellClass.SemiBold],
    },
  },
  'attributes.description': {
    colDef: {
      cellClass: [CellClass.Dark, CellClass.SemiBold],
      minWidth: 150,
      maxWidth: 600,
      resizable: true,
    },
    options: {
      autoSizeWidth: 260,
      showTooltip: true,
    },
  },
  'attributes.extRetail': {
    colDef: {
      cellClass: [CellClass.Dark, CellClass.Bold],
    },
  },
  'attributes.ids.asin': {
    colDef: {
      cellClass: [CellClass.Small],
    },
  },
  'attributes.ids.sku': {
    colDef: {
      cellClass: [CellClass.Small],
    },
  },
  'attributes.ids.upc': {
    colDef: {
      cellClass: [CellClass.Small],
    },
  },
  'attributes.item.gender': {
    colDef: {
      cellClass: [CellClass.Pill],
    },
  },
  'attributes.item.manufacturerPartNumber': {
    colDef: {
      cellClass: [CellClass.Small],
    },
  },
  'attributes.item.size': {
    colDef: {
      cellClass: [CellClass.Pill],
    },
  },
  'attributes.model': {
    colDef: {
      cellClass: [CellClass.Dark, CellClass.SemiBold],
      minWidth: 100,
      maxWidth: 600,
      resizable: true,
    },
    options: {
      autoSizeWidth: 260,
      showTooltip: true,
    },
  },
  itemCondition: {
    colDef: {
      cellClass: [CellClass.Small],
    },
  },
  'mobileDevice.sellerGrade': {
    colDef: {
      cellClass: [CellClass.Pill],
    },
  },
  quantity: {
    colDef: {
      cellClass: [CellClass.Dark, CellClass.ExtraBold],
    },
  },
  'uniqueIds.itemNumber': {
    colDef: {
      cellClass: [CellClass.Small],
    },
  },
}

// colId → width to apply after autoSizeAllColumns(), for columns that should
// start narrow but allow the user to expand them.
export const autoSizeWidthOverrides: ReadonlyArray<{
  key: string
  newWidth: number
}> = Object.entries(columnSpecs).flatMap(([colId, spec]) =>
  spec?.options?.autoSizeWidth != null
    ? [{ key: colId, newWidth: spec.options.autoSizeWidth }]
    : []
)
